import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { imageConverter } from '@/utils/image-resource-kit';
import type { TResource } from '../../services/canvas/edgeless-object';

const RESOURCE_THUMBNAIL_CLASS_NAME = 'resource-thumbnail';
const RESOURCE_THUMBNAIL_MAX_PIXEL_SIZE = 80;

export type ResourceThumbnailProps = {
    /** 舞台当前已经提交的 drawable。 */
    resource?: TResource;
    /** drawable 尚未就绪时优先展示的图层 IconFont。 */
    fallbackIcon?: ReactNode;
    /** 图层没有 IconFont 时使用的持久化资源。 */
    fallbackUrl?: string | null;
};

const thumbnailStyle: CSSProperties = {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'contain',
};

const placeholderStyle: CSSProperties = {
    display: 'block',
    width: '100%',
    height: '100%',
    borderRadius: '2px',
    background: 'currentcolor',
    opacity: 0.08,
};

const getDrawableSize = (resource: Exclude<TResource, null | void>) => {
    if (resource instanceof HTMLVideoElement) {
        return { width: resource.videoWidth, height: resource.videoHeight };
    }
    if (resource instanceof HTMLImageElement) {
        return { width: resource.naturalWidth, height: resource.naturalHeight };
    }
    return { width: resource.width, height: resource.height };
};

const DrawableThumbnail = ({
    resource,
    fallbackIcon,
}: {
    resource: Exclude<TResource, null | void>;
    fallbackIcon?: ReactNode;
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [failedResource, setFailedResource] = useState<TResource>();

    useLayoutEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        try {
            const { width, height } = getDrawableSize(resource);
            if (!width || !height) {
                setFailedResource(resource);
                return;
            }

            const scale = Math.min(
                RESOURCE_THUMBNAIL_MAX_PIXEL_SIZE / width,
                RESOURCE_THUMBNAIL_MAX_PIXEL_SIZE / height,
                1,
            );
            canvas.width = Math.max(1, Math.round(width * scale));
            canvas.height = Math.max(1, Math.round(height * scale));

            const context = canvas.getContext('2d');
            if (!context) {
                setFailedResource(resource);
                return;
            }

            context.clearRect(0, 0, canvas.width, canvas.height);
            context.drawImage(resource, 0, 0, canvas.width, canvas.height);
            setFailedResource(undefined);
        } catch (_) {
            setFailedResource(resource);
        }
    });

    const failed = failedResource === resource;
    return (
        <>
            <canvas
                ref={canvasRef}
                aria-hidden
                className={RESOURCE_THUMBNAIL_CLASS_NAME}
                style={failed ? { ...thumbnailStyle, display: 'none' } : thumbnailStyle}
            />
            {failed
                ? (fallbackIcon ?? (
                      <span aria-hidden className={RESOURCE_THUMBNAIL_CLASS_NAME} style={placeholderStyle} />
                  ))
                : null}
        </>
    );
};

/**
 * 把图层提供的 runtime drawable 或持久化 URL 绘制成与消费端容器无关的缩略内容。
 *
 * 本组件不查找图层、不选择回退来源，也不会触发 EdgelessObject 重新构建资源。
 */
export const ResourceThumbnail = ({ resource, fallbackIcon, fallbackUrl }: ResourceThumbnailProps) => {
    const [failedUrl, setFailedUrl] = useState<string>();

    if (resource) {
        const { width, height } = getDrawableSize(resource);
        if (fallbackIcon != null && width <= 1 && height <= 1) return fallbackIcon;
        return <DrawableThumbnail resource={resource} fallbackIcon={fallbackIcon} />;
    }
    if (fallbackIcon != null) return fallbackIcon;
    if (!fallbackUrl || failedUrl === fallbackUrl) {
        return <span aria-hidden className={RESOURCE_THUMBNAIL_CLASS_NAME} style={placeholderStyle} />;
    }
    const thumbnailUrl = imageConverter(fallbackUrl, RESOURCE_THUMBNAIL_MAX_PIXEL_SIZE);

    return (
        <img
            src={thumbnailUrl}
            alt=""
            className={RESOURCE_THUMBNAIL_CLASS_NAME}
            style={thumbnailStyle}
            onError={() => setFailedUrl(fallbackUrl)}
        />
    );
};
